import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, ChefHat, TrendingUp, Bell, Sparkles, LineChart, Camera, ScanSearch, BadgeEuro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { track } from "@/lib/analytics";
import HeroDemo from "@/components/landing/HeroDemo";
import { t } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const HOW = [
  [Camera, t("common.importez_votre_carte"), t("landing.photo_ou_pdf_depuis_votre_telephone")],
  [ScanSearch, t("landing.l_ia_lit_vos_plats_et_vos_prix"), t("landing.rubriques_plats_descriptions_et_prix_detec")],
  [BadgeEuro, t("landing.reperez_les_optimisations"), t("landing.prix_structure_et_marges_a_travailler")],
];

const FEATURES = [
  [FileText, t("landing.import_de_factures_par_ia"), t("landing.prenez_en_photo_ou_deposez_le_pdf_d_une_fa")],
  [ChefHat, t("landing.fiches_recettes_en_une_phrase"), t("landing.creez_vos_recettes_a_la_main_ou_decrivez_s")],
  [TrendingUp, t("landing.cout_matiere_et_marge_calcules_en_direct"), t("landing.pour_chaque_plat_mymenuprofit_calcule_le_c")],
  [Bell, t("landing.alertes_de_hausse_de_prix"), t("landing.a_chaque_nouvelle_facture_les_prix_sont_co")],
  [LineChart, t("landing.tableau_de_bord_de_rentabilite"), t("landing.visualisez_votre_cout_matiere_moyen_votre")],
  [Sparkles, t("landing.copilote_et_recommandations_ia"), t("landing.posez_vos_questions_en_francais_quels_plat")],
];

const nowrap = (s) => s.split(/(\S*-\S*)/).map((p, i) => (p.includes("-") ? <span key={i} className="whitespace-nowrap">{p}</span> : p));

export default function Landing() {
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    track("homepage_view");
  }, []);
  const click = (where) => track("menu_analysis_click", { location: where });

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-6 sm:px-6">
        <Logo />
        <nav className="flex items-center gap-1.5 sm:gap-2">
          <LanguageSwitcher />
          <Button variant="ghost" size="sm" asChild className="px-2 sm:h-10 sm:px-4" data-testid="landing-pricing-btn"><Link to="/tarifs">{t("landing.tarifs")}</Link></Button>
          <Button variant="ghost" asChild className="hidden sm:inline-flex" data-testid="landing-login-btn"><Link to="/login">{t("common.se_connecter")}</Link></Button>
          <Button size="sm" asChild className="sm:h-10 sm:px-4" data-testid="landing-register-btn"><Link to="/register">{t("landing.essai_gratuit")}</Link></Button>
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-5 sm:px-6">
        <section className="grid items-center gap-10 pb-12 pt-6 md:py-20 lg:grid-cols-[1.05fr_1fr] lg:gap-12 xl:py-24" data-testid="landing-hero">
          <div className="fade-up">
          <p className="eyebrow">{t("landing.analyse_de_carte_pour_restaurateurs")}</p>
          <h1 className="mt-3 max-w-3xl text-4xl font-extrabold tracking-tight sm:mt-4 sm:text-5xl lg:text-6xl" data-testid="landing-hero-title">
            {nowrap(t("landing.hero_title_before"))}<span className="text-emerald-500">{t("landing.hero_title_highlight")}</span>{t("landing.hero_title_after")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:mt-6 md:text-lg" data-testid="landing-hero-subtitle">
            {t("landing.importez_votre_menu_en_photo_ou_pdf_mymenu")}
          </p>
          <div className="mt-7 sm:mt-8">
            <Button size="lg" asChild className="h-14 w-full px-8 text-base shadow-lg shadow-emerald-500/20 active:scale-[0.98] sm:w-auto" data-testid="landing-analyze-cta-btn">
              <Link to="/analyser-ma-carte" onClick={() => click("hero")}>{t("landing.analyser_ma_carte_gratuitement")} <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground sm:text-left sm:text-sm" data-testid="landing-reassurance">{t("common.photo_ou_pdf_analyse_rapide_sans_saisie_ma")}</p>
          </div>
          <div className="mt-6 flex justify-center gap-4 text-sm sm:justify-start">
            <Link to="/login" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="landing-demo-btn">{t("landing.voir_la_demo")}</Link>
            <Link to="/register" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="landing-cta-btn">{t("common.creer_mon_compte")}</Link>
          </div>
          </div>
          <div className="fade-up [animation-delay:150ms]"><HeroDemo /></div>
        </section>
        <section className="mb-16 grid gap-3 sm:grid-cols-3" data-testid="landing-how">
          {HOW.map(([Icon, t, s], i) => (
            <div key={t} className="flex items-start gap-3 rounded-2xl border bg-card p-4 sm:p-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-sm font-bold text-emerald-500"><Icon className="h-4 w-4" /></span>
              <div><p className="text-sm font-semibold"><span className="text-muted-foreground">{i + 1}.</span> {t}</p><p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{s}</p></div>
            </div>
          ))}
        </section>
        <h2 className="mb-4 text-base font-bold md:text-lg">{t("landing.puis_suivez_la_rentabilite_de_chaque_plat")}</h2>
        <section className="grid gap-4 pb-16 md:grid-cols-2 lg:grid-cols-3" data-testid="landing-features">
          {FEATURES.map(([Icon, title, text]) => (
            <article key={title} className="rounded-2xl border bg-card p-6 transition-colors hover:border-emerald-500/40">
              <Icon className="h-5 w-5 text-emerald-500" />
              <h2 className="mt-4 text-base font-bold md:text-lg">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
            </article>
          ))}
        </section>
        <section className="mb-16 rounded-2xl border bg-card p-6 text-center sm:p-10" data-testid="landing-bottom-cta">
          <p className="text-xl font-extrabold tracking-tight sm:text-2xl">{t("landing.decouvrez_ce_que_votre_carte_peut_vous_rap")}</p>
          <Button size="lg" asChild className="mt-5 h-12 w-full text-base sm:w-auto" data-testid="landing-analyze-cta-bottom-btn">
            <Link to="/analyser-ma-carte" onClick={() => click("bottom")}>{t("landing.analyser_ma_carte_gratuitement")}</Link>
          </Button>
        </section>
        <section className="max-w-3xl pb-24" data-testid="landing-why">
          <h2 className="text-base font-bold md:text-lg">{t("landing.pense_pour_une_seule_question_la_rentabili")}</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {t("landing.mymenuprofit_n_est_ni_une_caisse_enregistr")}
          </p>
        </section>
      </main>
      <footer className="border-t py-8 text-center text-xs text-muted-foreground">{t("common.mymenuprofit_l_intelligence_de_menu_pour_r")}</footer>
    </div>
  );
}
